import { Feature, Point, Position } from "geojson";
import { useCallback, useState } from "react";

import { PickupPointProperties } from "~/modules/plannerV2/components/map-layers/PickupPointLayer";

import usePlannerV2FieldworkerMergePickupDropoffLocation from "./usePlannerV2FieldworkerMergePickupDropoffLocation";
import usePlannerV2FieldworkerMovePickupDropoffLocation from "./usePlannerV2FieldworkerMovePickupDropoffLocation";
import usePlannerV2FieldworkerRemovePickupDropoffLocation from "./usePlannerV2FieldworkerRemovePickupDropoffLocation";

export function usePlannerV2RoutePickupActions({
    travelGroupId,
    onSuccess,
}: {
    travelGroupId: string;
    onSuccess: (plannerTravelGroupId: string) => void;
}) {
    const {
        moveFieldworker,
        error: pickupPointMovedError,
        loading: isLoadingPickupMove,
    } = usePlannerV2FieldworkerMovePickupDropoffLocation(onSuccess);
    const {
        mergeFieldworkers,
        error: pickupPointMergedError,
        loading: isLoadingPickupMerge,
    } = usePlannerV2FieldworkerMergePickupDropoffLocation(onSuccess);

    const { removePickupPoint, loadingToRemoveShiftId } = usePlannerV2FieldworkerRemovePickupDropoffLocation(onSuccess);
    const [pendingPickupAction, setPendingPickupAction] = useState<{
        type: "merge" | "move";
        location: Position;
    } | null>(null);

    const handleWorkerDragged = useCallback(
        async (
            feature: Feature<Point, PickupPointProperties>,
            coordinates: Position,
            droppedOnFeature?: Feature<Point, PickupPointProperties>,
        ) => {
            if (droppedOnFeature) {
                setPendingPickupAction({
                    type: "merge",
                    location: droppedOnFeature.geometry.coordinates,
                });

                await mergeFieldworkers({
                    plannerTravelGroupId: travelGroupId,
                    destinationPlannerShiftId: droppedOnFeature.properties?.shiftIds[0],
                    sourcePlannerShiftId: feature?.properties?.shiftIds[0],
                });

                setPendingPickupAction(null);
            } else {
                setPendingPickupAction({
                    type: "move",
                    location: coordinates,
                });

                await moveFieldworker({
                    plannerShiftId: feature?.properties?.shiftIds[0],
                    point: { coordinates, type: "point" },
                    plannerTravelGroupId: travelGroupId,
                });

                setPendingPickupAction(null);
            }
        },
        [moveFieldworker, mergeFieldworkers, travelGroupId],
    );

    return {
        handleWorkerDragged,
        removePickupPoint,

        loadingToRemoveShiftId,

        pendingPickupAction: pendingPickupAction && {
            ...pendingPickupAction,
            loading: isLoadingPickupMerge || isLoadingPickupMove,
            error: pickupPointMovedError || pickupPointMergedError,
        },
    };
}
